import { isNumber, omit } from "@shared/common/utils/typing";
import { GetLinePartsResult, IGetLinePartsStrategy } from "../IGetLinePartsStrategy";

type NumberOfPixels = "px";
type Ratio = "ratio";
type Rest = "*";

export type CutLineStrategyPart = {
  type: NumberOfPixels | Ratio | Rest;
  value?: number;
  /**
   * A higher number means higher precedence meaning lower values will be adjusted first to fit the available space.
   */
  precedence?: number;
  hideIfShortened?: boolean;
  minLength?: number;
};

export type WithContext<T extends Record<string, unknown>> = {
  /**
   * The context of the part. This is used to identify the part in the result.
   */
  context?: T;
};

export type CutLineStrategyPartWithContext<T extends Record<string, unknown>> = WithContext<T> &
  CutLineStrategyPart;

type CutLineStrategyPartWithContentAndOrderId<T extends Record<string, unknown>> =
  CutLineStrategyPartWithContext<T> & { orderId: number };

type UnkownContext = Record<string, unknown>;

/**
 * Splits the line into segments based on the given parts. The lengths can be in pixels, ratios or rest of the space.
 * The lengths can also have a precedence which means that the lower precedence will be adjusted first to fit the available space.
 */
export class CutLineStrategy<
  T extends Record<string, unknown>,
> implements IGetLinePartsStrategy<T> {
  private _parts: CutLineStrategyPartWithContext<T>[];

  constructor(parts: CutLineStrategyPartWithContext<T>[] | (string | number)[]) {
    if (parts.length === 0) {
      throw new Error("items cannot be empty");
    }
    if (typeof parts[0] === "string" || typeof parts[0] === "number") {
      this._parts = (parts as string[]).map((w) => {
        return CutLineStrategyPartHelper.Parse(w);
      });
    } else {
      this._parts = parts as CutLineStrategyPartWithContext<T>[];
    }
  }

  private static getTotalLength(parts: CutLineStrategyPart[]) {
    return parts.reduce((acc, w) => {
      if (w.type === "px") {
        return acc + (w.value ?? 0);
      }
      return acc;
    }, 0);
  }

  private static sortByPrecedence(
    this: void,
    a: CutLineStrategyPartWithContentAndOrderId<UnkownContext>,
    b: CutLineStrategyPartWithContentAndOrderId<UnkownContext>,
  ) {
    if (a.precedence === undefined || b.precedence === undefined) {
      throw new Error("Precedence is required");
    }
    if (a.precedence === b.precedence) {
      return a.orderId - b.orderId;
    }

    return a.precedence - b.precedence;
  }

  private static min(a: number, b: number) {
    return a < b ? a : b;
  }

  private static max(a: number, b: number) {
    return a > b ? a : b;
  }

  private static between(min: number, max: number) {
    return (value: number) => {
      return this.min(this.max(min, value), max);
    };
  }

  private adjustLengthsToPrecedence(lineLength: number): CutLineStrategyPartWithContext<T>[] {
    const onLine = CutLineStrategy.between(0, lineLength);

    const sortedByPrecedenceWithOrderIds = this._parts
      .map((w, i) => ({ ...w, orderId: i, precedence: w.precedence ?? 0 }))
      .sort(CutLineStrategy.sortByPrecedence);

    const groupedByPrecedence = Object.groupBy(
      sortedByPrecedenceWithOrderIds,
      (item) => item.precedence,
    );
    const groupKeys = Object.keys(groupedByPrecedence);

    const adjustedToPrecedence = groupKeys
      .map((precedence) => parseInt(precedence))
      .toSorted()
      .reduce((toAdjust, precedence) => {
        const length = CutLineStrategy.getTotalLength(toAdjust);
        if (length <= lineLength) {
          return toAdjust;
        }

        const others = toAdjust.filter((part) => part.precedence !== precedence);
        const othersLength = CutLineStrategy.getTotalLength(others);

        const currentsLength = length - othersLength;
        const adjustedCurrents = groupedByPrecedence[precedence]!.map((part) => {
          if (part.type !== "px" || !part.value) {
            return part;
          }
          if (length - currentsLength > lineLength || part.hideIfShortened === true) {
            return { ...part, value: 0 };
          }
          const availableLength = lineLength - othersLength;
          const ratio = availableLength / currentsLength;

          return { ...part, value: onLine(part.value * ratio) };
        });
        return [...others, ...adjustedCurrents];
      }, sortedByPrecedenceWithOrderIds);

    return adjustedToPrecedence
      .toSorted((a, b) => a.orderId - b.orderId)
      .map((part) => omit(part, "orderId"));
  }

  private get hasPrecedence() {
    return this._parts.some((w) => w.precedence !== undefined);
  }

  public getLineParts(lineLengthInPx: number) {
    const lengths = this.hasPrecedence
      ? this.adjustLengthsToPrecedence(lineLengthInPx)
      : this._parts;

    return this.partsToLineParts(lengths, lineLengthInPx);
  }

  private partsToLineParts(parts: CutLineStrategyPartWithContext<T>[], lineLengthInPx: number) {
    const onLine = CutLineStrategy.between(0, lineLengthInPx);
    let currentX = 0;

    const numberOfRestLengths = parts.filter((w) => w.type === "*").length;

    const restLengthsValue =
      CutLineStrategy.max(lineLengthInPx - CutLineStrategy.getTotalLength(parts), 0) /
      numberOfRestLengths;

    return parts.map<GetLinePartsResult<T>>((w) => {
      const value = w.value ?? 0;
      if (w.type === "px") {
        const result = {
          linePart: {
            start: onLine(currentX),
            end: onLine(currentX + value),
          },
          context: w.context,
        } as GetLinePartsResult<T>;
        currentX += value;
        return result;
      }
      if (w.type === "ratio") {
        const result = {
          linePart: {
            start: onLine(currentX),
            end: onLine(currentX + value * lineLengthInPx),
          },
          context: w.context,
        } as GetLinePartsResult<T>;
        currentX += value * lineLengthInPx;
        return result;
      }
      if (w.type === "*") {
        const result = {
          linePart: {
            start: onLine(currentX),
            end: onLine(currentX + restLengthsValue),
          },
          context: w.context,
        } as GetLinePartsResult<T>;
        currentX += restLengthsValue;
        return result;
      }
      throw new Error("Invalid CutLineStrategyPart type");
    });
  }
}

class CutLineStrategyPartHelper {
  public static px(value: number): CutLineStrategyPart {
    return { type: "px", value: value };
  }

  public static ratio(value: number): CutLineStrategyPart {
    return { type: "ratio", value: value };
  }

  public static rest(): CutLineStrategyPart {
    return { type: "*", value: 1 };
  }

  public static Parse(value: string | number): CutLineStrategyPart {
    if (isNumber(value)) {
      return CutLineStrategyPartHelper.ratio(value);
    }
    if (value.endsWith("px")) {
      return CutLineStrategyPartHelper.px(parseInt(value.substring(0, value.length - 2)));
    }
    if (value === "*") {
      return CutLineStrategyPartHelper.rest();
    }

    throw new Error("Invalid CutLineStrategyPart string");
  }
}
